// Builder Residual Risk Service - Residual risk calculation for Builder assessments
// Uses Builder-specific Redux state (builderRisks, not explorerRisks or modelRisks)
import {
  generateResidualRisk,
  analyzeActionPlan,
  saveResidualRisk,
  RESIDUAL_RISK_RATINGS,
} from '../../redux/slices/residualRiskSlice';

export class BuilderResidualRiskService {
  constructor(dispatch, modelId, assessmentUuid, liveUpdateCallback = null) {
    this.dispatch = dispatch;
    this.modelId = modelId;
    this.assessmentUuid = assessmentUuid;
    this.liveUpdateCallback = liveUpdateCallback;
  }

  async executeResidualAssessment(areaKey, areaDefinition, inheritedRisks, progressCallback) {
    try {
      console.log(`[BuilderResidualRiskService] Starting residual assessment for area: ${areaKey}`);
      if (progressCallback) progressCallback('residual');
      await new Promise(resolve => setTimeout(resolve, 600));

      // Get fresh Redux state
      const getState = () => this.dispatch((dispatch, getState) => getState());
      const currentState = getState();

      // Access Builder risks (not explorerRisks or modelRisks)
      const builderRisks = currentState.riskItems?.byAssessment?.[this.assessmentUuid]?.builderRisks || {};
      const areaRisks = builderRisks[areaKey]?.areaRisks || {};

      const evaluatedRisks = [];

      console.log(`[BuilderResidualRiskService] State source for area: ${areaKey}`);
      console.log(`[BuilderResidualRiskService] Assessment UUID:`, this.assessmentUuid);
      console.log(`[BuilderResidualRiskService] Available risk categories:`, Object.keys(areaRisks));

      Object.entries(areaRisks).forEach(([category, riskArray]) => {
        if (Array.isArray(riskArray)) {
          riskArray.forEach(risk => {
            if (risk.status === 'Saved' && risk.priority) {
              evaluatedRisks.push({
                ...risk,
                areaKey,
                category
              });
            }
          });
        }
      });

      console.log(`[BuilderResidualRiskService] Found ${evaluatedRisks.length} evaluated risks`);

      const freshControlState = currentState.controls || {};

      console.log(`[BuilderResidualRiskService] Checking for saved controls...`);
      console.log(`[BuilderResidualRiskService] Available savedControls keys:`, Object.keys(freshControlState?.savedControls || {}));

      const risksWithActions = evaluatedRisks.filter(risk => {
        if (risk.areaKey !== areaKey) {
          console.log(`[BuilderResidualRiskService] SKIPPING risk from wrong area: "${risk.riskName}"`);
          return false;
        }

        const allControls = freshControlState?.savedControls?.[risk.riskId] || [];
        const modelControls = allControls.filter(control => control.modelId === this.modelId);
        console.log(`[BuilderResidualRiskService] Risk "${risk.riskName}" (${risk.riskId}): ${modelControls.length} saved controls`);
        return modelControls.length > 0;
      });

      console.log(`[BuilderResidualRiskService] Found ${risksWithActions.length} risks with actions for residual assessment`);

      let residualAssessments = 0;

      for (const risk of risksWithActions) {
        try {
          console.log(`[BuilderResidualRiskService] Starting parallel assessment for: ${risk.riskName}`);
          const startTime = Date.now();

          await Promise.all([
            this.dispatch(analyzeActionPlan({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId,
              riskName: risk.riskName,
              assessmentType: 'navigator'
            })),
            this.dispatch(generateResidualRisk({
              assessmentUuid: this.assessmentUuid,
              modelId: this.modelId,
              riskId: risk.riskId,
              assessmentType: 'navigator'
            }))
          ]);

          const elapsed = Date.now() - startTime;
          console.log(`[BuilderResidualRiskService] Parallel assessment completed in ${elapsed}ms for: ${risk.riskName}`);

          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('actionPlanAnalyzed', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              areaKey
            });
          }

          const updatedState = getState();
          const residualRiskData = updatedState.residualRiskRTK?.byAssessment?.[this.assessmentUuid]?.[risk.riskId] || {};

          let residualRating = residualRiskData.residual_rating;
          if (!residualRating || !RESIDUAL_RISK_RATINGS.includes(residualRating)) {
            console.error(`[BuilderResidualRiskService] Invalid/missing rating "${residualRating}" - skipping save for this risk`);
            continue;
          }

          const residualData = {
            modelId: this.modelId,
            assessmentUuid: this.assessmentUuid,
            riskId: risk.riskId,
            riskName: risk.riskName,
            priority: risk.priority,
            evaluator_id: 'navigator-autopilot@agenthuginn.ai',
            comments: `Generated by BuilderRiskAutopilot for area: ${areaKey}`,
            residual_rating: residualRating,
            residual_explanation: residualRiskData.residual_explanation || '',
            action_plan_rating: residualRiskData.action_plan_rating || '',
            action_plan_analysis: residualRiskData.action_plan_analysis || '',
            action_plan_short_summary: residualRiskData.action_plan_short_summary || '',
            assessmentType: 'navigator'
          };

          this.dispatch(saveResidualRisk(residualData));
          residualAssessments++;

          if (this.liveUpdateCallback) {
            this.liveUpdateCallback('residualRiskCalculated', {
              riskName: risk.riskName,
              riskId: risk.riskId,
              residualRating: residualRating,
              actionPlanRating: residualRiskData.action_plan_rating || '',
              assessedCount: residualAssessments,
              totalRisks: risksWithActions.length,
              areaKey
            });
          }

        } catch (residualError) {
          console.error(`[BuilderResidualRiskService] Residual assessment failed for ${risk.riskName}:`, residualError);
        }
      }

      return {
        risksWithActions: risksWithActions.length,
        residualAssessments,
        areaKey
      };

    } catch (error) {
      console.error('[BuilderResidualRiskService] Residual assessment failed:', error);
      throw error;
    }
  }
}
